import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/lib/i18n';
import { toast } from 'sonner';
import { Flame, Eye, EyeOff, ArrowRight, Globe } from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, lang, setLanguage } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error(t.fillAllFields); return; }
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(user.role === 'superadmin' ? '/superadmin' : '/dashboard');
    } catch {
      toast.error(t.invalidCredentials);
    } finally { setLoading(false); }
  };

  const quickLogin = (em, pw) => { setEmail(em); setPassword(pw); };

  return (
    <div className="min-h-screen bg-[#09090b] flex" data-testid="login-page">
      {/* Left panel */}
      <div className="hidden lg:flex lg:flex-1 items-center justify-center bg-gradient-to-br from-[#09090b] to-[#121214] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wMykiLz48L3N2Zz4=')] opacity-60" />
        <div className="text-center z-10 px-12">
          <div className="w-20 h-20 mx-auto mb-8 rounded-2xl bg-gradient-to-br from-[#EF694C] to-[#d65d43] flex items-center justify-center shadow-[0_0_40px_rgba(239,105,76,0.4)]">
            <Flame className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-heading font-bold text-5xl text-white tracking-tight mb-4">FyreSupport</h1>
          <p className="text-lg text-[#a1a1aa] max-w-md">{t.loginTagline}</p>
          <p className="text-xs text-[#3f3f46] mt-8 uppercase tracking-widest">by OnFyre Marketing</p>
        </div>
      </div>

      {/* Right panel — Form */}
      <div className="w-full lg:w-[480px] flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          {/* Language toggle */}
          <div className="flex justify-end mb-6">
            <button
              onClick={() => setLanguage(lang === 'de' ? 'en' : 'de')}
              data-testid="login-language-toggle"
              className="flex items-center gap-1.5 h-8 px-2.5 text-[#52525b] hover:text-white hover:bg-white/5 rounded-lg transition-all text-xs font-medium"
            >
              <Globe className="w-3.5 h-3.5" />
              <span>{lang === 'de' ? 'DE' : 'EN'}</span>
            </button>
          </div>

          {/* Logo for mobile */}
          <div className="lg:hidden mb-8 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-gradient-to-br from-[#EF694C] to-[#d65d43] flex items-center justify-center shadow-[0_0_30px_rgba(239,105,76,0.3)]">
              <Flame className="w-7 h-7 text-white" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-white">FyreSupport</h1>
          </div>

          <h2 className="font-heading font-bold text-2xl text-white mb-1">{t.loginTitle}</h2>
          <p className="text-sm text-[#a1a1aa] mb-8">{t.loginSubtitle}</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#d4d4d8] mb-2">{t.email}</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                data-testid="login-email-input" placeholder="name@example.com"
                className="w-full h-11 bg-[#121214] border border-white/10 rounded-lg px-4 text-white text-sm focus:outline-none focus:border-[#EF694C] focus:ring-1 focus:ring-[#EF694C]/30 transition-all" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#d4d4d8] mb-2">{t.password}</label>
              <div className="relative">
                <input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  data-testid="login-password-input" placeholder="••••••••"
                  className="w-full h-11 bg-[#121214] border border-white/10 rounded-lg px-4 pr-10 text-white text-sm focus:outline-none focus:border-[#EF694C] focus:ring-1 focus:ring-[#EF694C]/30 transition-all" />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#52525b] hover:text-white transition-colors">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full h-11 bg-[#EF694C] hover:bg-[#d65d43] text-white font-semibold rounded-lg text-sm shadow-[0_0_20px_rgba(239,105,76,0.3)] transition-all active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2">
              {loading ? t.loading : <>{t.signIn} <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

        </div>
      </div>
    </div>
  );
}
